import { PapiGallection, PapiModuleFolderDeviations } from '@wix/da-papi-types';
import { ALL_FOLDER_ID } from '@wix/da-url';
import { normalize, schema } from '@wix/da-shared-react/pkg/redux/normalizr';
import { GallectionType } from '../../types/gallection';
import {
  galleryFolderSchema,
  collectionFolderSchema,
  stashFolderSchema,
} from '@wix/da-shared-react/pkg/redux/normalizr/schemas/folder';
import {
  GALLERY_FOLDER_IDS_STREAM,
  COLLECTION_FOLDER_IDS_STREAM,
  PRIVATE_COLLECTION_FOLDER_IDS_STREAM,
} from '../streams';

// UI uses "relevance" but PAPI uses "personalized"
export type GalleryOrder = 'newest' | 'relevance' | 'oldest';
type PapiGalleryOrder = NonNullable<PapiModuleFolderDeviations['effectiveOrder']>;

const ORDER_UI_TO_PAPI: Record<GalleryOrder, PapiGalleryOrder> = {
  relevance: 'personalized',
  newest: 'newest',
  oldest: 'oldest',
} as const;

const ORDER_PAPI_TO_UI: Record<PapiGalleryOrder, GalleryOrder> = {
  personalized: 'relevance',
  newest: 'newest',
  oldest: 'oldest',
} as const;

function getUrlParam(search: string, paramName: string) {
  return new URLSearchParams(search).get(paramName) ?? undefined;
}

function hasGalleryOrder(order: string): order is GalleryOrder {
  return Object.prototype.hasOwnProperty.call(ORDER_UI_TO_PAPI, order);
}

export const getGalleryOrderFromUrl = (order?: string | null): GalleryOrder | undefined => {
  return order && hasGalleryOrder(order) ? order : undefined;
};

export const getGalleryOrderFromSearch = (search: string): GalleryOrder | undefined => {
  return getGalleryOrderFromUrl(getUrlParam(search, 'order'));
};

export const getDeviationIdSeedFromSearch = (search: string) => {
  const value = getUrlParam(search, 'deviationid');
  const parsedValue = value ? parseInt(value, 10) : NaN;

  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

/**
 * Maps UI order format to PAPI format for API requests.
 *
 * The UI uses 'relevance' to represent personalized ordering, while the PAPI
 * expects 'personalized'. This function converts UI format to PAPI format.
 * Redux streams store order in PAPI format for consistency with the API.
 *
 * @param order - UI format: 'relevance' | 'newest' | undefined
 * @returns PAPI format: 'personalized' | 'newest' | undefined
 *
 * @example
 * mapOrderToPAPI('relevance') // Returns 'personalized'
 * mapOrderToPAPI('newest')    // Returns 'newest'
 * mapOrderToPAPI('invalid')   // Returns undefined
 */
export const mapOrderToPAPI = (order?: string): PapiGalleryOrder | undefined => {
  return order && hasGalleryOrder(order) ? ORDER_UI_TO_PAPI[order] : undefined;
};

/**
 * Maps PAPI order format to UI format for display.
 *
 * Converts the PAPI 'personalized' value to the UI-friendly 'relevance' label.
 * This is used when reading order from Redux streams (which store PAPI format)
 * to display in UI components like the GalleryOrderDropdown.
 *
 * @param order - PAPI format: 'personalized' | 'newest' | undefined
 * @returns UI format: 'relevance' | 'newest' | undefined
 *
 * @example
 * mapOrderFromPAPI('personalized') // Returns 'relevance'
 * mapOrderFromPAPI('newest')       // Returns 'newest'
 */
export const mapOrderFromPAPI = (order?: PapiGalleryOrder): GalleryOrder | undefined => {
  return order ? ORDER_PAPI_TO_UI[order] : undefined;
};

const schemas: { [k in GallectionType]: schema.Entity } = {
  [GallectionType.GALLERY]: galleryFolderSchema,
  [GallectionType.COLLECTION]: collectionFolderSchema,
  [GallectionType.PRIVATE_COLLECTION]: collectionFolderSchema,
  [GallectionType.STASH]: stashFolderSchema,
};
const DEFAULT_FOLDERS = [ALL_FOLDER_ID];

export function getSchemaForGallectionType(gallectionType: GallectionType) {
  return schemas[gallectionType] as schema.Entity;
}

export function normalizeGallectionList(
  folders: PapiGallection[] | undefined,
  type?: GallectionType
) {
  const gallectionType = type ?? (folders?.[0]?.type as GallectionType | undefined);
  if (!gallectionType) {
    return { result: folders, entities: {} };
  }

  return normalize(folders, [getSchemaForGallectionType(gallectionType)]);
}

export const getGallectionTypeFromSection = (
  gallectionSection: 'gallery' | 'favourites' | 'private_collections'
) =>
  ({
    gallery: GallectionType.GALLERY,
    favourites: GallectionType.COLLECTION,
    private_collections: GallectionType.PRIVATE_COLLECTION,
  })[gallectionSection];

export function getStreamIdForGallectionType(gallectionType: GallectionType) {
  return {
    [GallectionType.GALLERY]: GALLERY_FOLDER_IDS_STREAM,
    [GallectionType.COLLECTION]: COLLECTION_FOLDER_IDS_STREAM,
    [GallectionType.PRIVATE_COLLECTION]: PRIVATE_COLLECTION_FOLDER_IDS_STREAM,
  }[gallectionType];
}

function isFolderNotEmpty({ size, totalItemCount = size, folderId }: PapiGallection) {
  return (
    // has deviations, self or subfolders
    totalItemCount > 0 ||
    // always needs to be there and/or shown
    DEFAULT_FOLDERS.includes(folderId)
  );
}

/** Return only folders have deviations, subfolders, or are a default (like 'all') */
export function filterOutEmpty(folders: PapiGallection[]) {
  if (!Array.isArray(folders)) {
    return [];
  }
  return (
    folders
      // filter out empty root folders
      .filter(folder => isFolderNotEmpty(folder))
      // filter out empty subfolders
      .map(folder => ({
        ...folder,
        subfolders: folder.subfolders?.filter(isFolderNotEmpty),
      }))
  );
}

export function filterOutEmptyAndSubfolders(folders: PapiGallection[]) {
  return folders.filter(folder => !folder.parentId && isFolderNotEmpty(folder));
}
